import { createSlice } from "@reduxjs/toolkit";
import {
  fetchEmergencyContacts,
  addEmergencyContact,
  updateEmergencyContact,
  getEmergencyContactById,
  deleteEmergencyContact,
} from "../thunks/emergencyContactThunks";

const initialState = {
  byId: {},
  allIds: [],
  isLoading: false,
  error: null,
  currentUserId: null,
  ui: {
    addModalOpen: false,
  },
};

/* Helpers */
const getId = (obj) => obj?._id ?? obj?.id; // ✅ _id prioritaire
const upsertOne = (state, item) => {
  const id = getId(item);
  if (!id) return;
  state.byId[id] = { ...item, _key: id };
  if (!state.allIds.includes(id)) state.allIds.unshift(id);
};

const removeOne = (state, id) => {
  if (!id) return;

  delete state.byId[id];
  state.allIds = state.allIds.filter((x) => x !== id);
};

/* =======================
   Slice
======================= */
const emergencyContactSlice = createSlice({
  name: "emergencyContacts",
  initialState,
  reducers: {
    setAddModalOpen(state, action) {
      state.ui.addModalOpen = Boolean(action.payload);
    },

    clearEmergencyContacts(state) {
      state.byId = {};
      state.allIds = [];
      state.isLoading = false;
      state.error = null;
      state.currentUserId = null;
      state.ui.addModalOpen = false;
    },
  },

  extraReducers: (builder) => {
    /* ---------- FETCH ALL ---------- */
    builder
      .addCase(fetchEmergencyContacts.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })

      .addCase(fetchEmergencyContacts.fulfilled, (state, action) => {
        const { userId, items = [] } = action.payload || {};

        state.byId = {};
        state.allIds = [];

        items.forEach((item) => upsertOne(state, item));

        state.currentUserId = userId || null;
        state.isLoading = false;
      })

      // ⭐⭐ CORRECTION CLÉ : on vide le state si l’API échoue ⭐⭐
      .addCase(fetchEmergencyContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.byId = {};
        state.allIds = [];
        state.currentUserId = null;
        state.error =
          action.payload ||
          String(action.error?.message || "fetchEmergencyContacts failed");
      });

    /* ---------- ADD ---------- */
    builder
      .addCase(addEmergencyContact.pending, (state) => {
        state.error = null;
      })

      .addCase(addEmergencyContact.fulfilled, (state, action) => {
        upsertOne(state, action.payload);
        state.ui.addModalOpen = false;
      })

      .addCase(addEmergencyContact.rejected, (state, action) => {
        state.error =
          action.payload ||
          String(action.error?.message || "addEmergencyContact failed");
      });

    /* ---------- UPDATE ✅ ---------- */
    builder
      .addCase(updateEmergencyContact.pending, (state) => {
        state.error = null;
      })
      .addCase(updateEmergencyContact.fulfilled, (state, action) => {
        upsertOne(state, action.payload);
      })
      .addCase(updateEmergencyContact.rejected, (state, action) => {
        state.error =
          action.payload ||
          String(action.error?.message || "updateEmergencyContact failed");
      });

    /* ---------- GET ONE ---------- */
    builder
      .addCase(getEmergencyContactById.pending, (state) => {
        state.error = null;
      })

      .addCase(getEmergencyContactById.fulfilled, (state, action) => {
        upsertOne(state, action.payload);
      })

      .addCase(getEmergencyContactById.rejected, (state, action) => {
        state.error =
          action.payload ||
          String(action.error?.message || "getEmergencyContactById failed");
      });
/* ---------- DELETE ✅ FIX ---------- */
builder
  .addCase(deleteEmergencyContact.pending, (state) => {
    state.error = null;
  })
  .addCase(deleteEmergencyContact.fulfilled, (state, action) => {
    // ✅ payload id OR fallback to meta.arg.id
    const id =
      action.payload?.id ||
      action.meta?.arg?.id;

    removeOne(state, String(id));
  })
  .addCase(deleteEmergencyContact.rejected, (state, action) => {
    state.error =
      action.payload ||
      String(action.error?.message || "deleteEmergencyContact failed");
  });

  },
});

export const { setAddModalOpen, clearEmergencyContacts } =
  emergencyContactSlice.actions;

export default emergencyContactSlice.reducer;

/* =================== SELECTORS (STORE KEY FIX) =================== */
export const selectEmergencyState = (state) => state.emergencyContacts;

export const selectEmergencyContacts = (state) => {
  const st = selectEmergencyState(state) || initialState;
  const byId = st.byId || {};
  const allIds = st.allIds || [];
  return allIds.map((id) => byId[id]).filter(Boolean);
};

export const selectEmergencyLoading = (state) =>
  !!selectEmergencyState(state)?.isLoading;

export const selectEmergencyError = (state) =>
  selectEmergencyState(state)?.error ?? null;

export const selectEmergencyStatus = (state) =>
  selectEmergencyLoading(state) ? "loading" : "idle";

export const selectEmergencyContactById = (id) => (state) =>
  selectEmergencyState(state)?.byId?.[id];

export const selectAddModalOpen = (state) =>
  !!selectEmergencyState(state)?.ui?.addModalOpen;
